/* eslint-disable */
//@ts-nocheck
import React, { useEffect, useLayoutEffect, useState, useReducer } from "react";
import { ConfigProvider, App as AntdApp } from "antd";
import { getAntdConfigProps } from "themes/getAntdConfigProps";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import i18next from "i18next";
import enUS from "antd/locale/en_US";
import arEG from "antd/locale/ar_EG";
import urPK from "antd/locale/ur_PK";
import hiIN from "antd/locale/hi_IN";
import bnBD from "antd/locale/bn_BD";
import frFR from "antd/locale/fr_FR";
import zhCN from "antd/locale/zh_CN";

import { localeMapValue } from "types/layout";
import { AppLocale, applyDayjsLocale, currentLocale, isRtl, localeFontFamily } from "utils/locale";
import { Organization } from "types/app";
import axios from "axios";
import { Locale } from "antd/lib/locale";
import { mixPanelConfig } from "../../utils/MispanelConfig";
import { openpanelConfig } from "../../utils/openpanel";
import { PlansContext } from "contexts/plans";
import { QueryProvider } from "contexts/QueryProvider";

// Theme tokens are sourced via getAntdConfigProps() (QERP-aware) at render time.

const arLocaleWithShortDays: Locale = {
  ...arEG,
  DatePicker: arEG.DatePicker
    ? {
        ...arEG.DatePicker,
        lang: {
          ...arEG.DatePicker.lang,
          shortWeekDays: ["ح", "ن", "ث", "ر", "خ", "ج", "س"],
        },
      }
    : undefined,
};

i18next
  .use(initReactI18next)
  .init({
    resources: {
      [window.I18n.locale]: {
        translation: window.I18n,
      },
    },
    lng: window.I18n.locale,
  })
  .catch((err) => console.error("i18n initialization failed", err));

// One entry per locale the app renders. Direction and font come from utils/locale so there is one
// place to change when a locale is added — see the comments there for why RTL is ar+ur and why
// Hindi and Bengali need a Noto fallback.
//
// A Record rather than the Map this used to be, because a miss here is silent and expensive:
// `localeMap.get("hi")` returned undefined, so ConfigProvider was handed `direction: undefined`
// and no font token at all. Lookups go through resolveLocale(), which always lands on an entry.
// antd has no zh-Hans pack — zh_CN is its Simplified Chinese one, and its French pack is fr_FR.
// The app's own tag stays zh-Hans; only the dependency's spelling is regional.
const antdLocales: Record<AppLocale, Locale> = {
  en: enUS,
  ar: arLocaleWithShortDays,
  ur: urPK,
  hi: hiIN,
  bn: bnBD,
  fr: frFR,
  "zh-Hans": zhCN,
};

const localeMap = Object.fromEntries(
  Object.entries(antdLocales).map(([code, locale]) => [
    code,
    {
      locale,
      dir: isRtl(code) ? "rtl" : "ltr",
      fontFamily: localeFontFamily(code),
    },
  ]),
) as Record<AppLocale, localeMapValue>;

const resolveLocale = (language?: string): localeMapValue =>
  localeMap[currentLocale(language) as AppLocale] ?? localeMap.en;

// dayjs supplies the antd date pickers' month and weekday names, so it has to agree with the pack
// chosen above. Applied here as well as in the effect below because the first render happens before
// effects run, and read straight off window.I18n — the same value i18next was just initialised with
// — so it does not depend on when i18next publishes `language`.
applyDayjsLocale(window.I18n.locale);

// Built at render time so getAntdConfigProps() can read the qerp-theme body class; non-QERP output unchanged.
export const buildAntdConfigProps = () => {
  const { theme } = getAntdConfigProps();
  const entry = resolveLocale(i18next.language);
  const token = { ...theme.token, fontFamily: entry.fontFamily };
  return {
    theme: { components: theme.components, token },
    locale: entry.locale,
    direction: entry.dir,
  };
};

interface AppProps {
  modulePath: string;
  pagePath: string;
  isPos: boolean;
}

const App: React.FC<AppProps> = ({ modulePath, pagePath, ...props }) => {
  const [Page, setPage] = useState<React.ReactElement | null>(null);

  useQuery<Organization>({
    queryKey: ["OrgDetails"],
    queryFn: async () => {
      const response = await axios.get<Organization>(
        "/tenant/dashboards/current_organization.json",
      );
      return response.data;
    },
    enabled: Boolean(Page),
  });

  useLayoutEffect(() => {
    import(`pages/${modulePath}/${pagePath}`)
      .then((Module) => {
        setPage(<Module.default {...props} />);
      })
      .catch(() => {
        throw new Error(`Couldn't find the page`);
      });
  }, []);

  useEffect(() => {
    applyDayjsLocale(i18next.language);
  }, [i18next.language]);

  useEffect(() => {
    mixPanelConfig();
    // Additive: OpenPanel joins Mixpanel rather than replacing it, and no-ops
    // when FRONT_END_OPENPANEL_CLIENT_ID / _API_URL aren't set.
    openpanelConfig();
  }, []);

  return Page;
};

const AppWrapper: React.FC<AppProps> = ({ isPos, ...props }) => (
  <PlansContext.Provider value={{ isPos }}>
    <I18nextProvider i18n={i18next}>
      <ConfigProvider {...buildAntdConfigProps()}>
        <QueryProvider>
          <AntdApp>
            <App {...props} />
          </AntdApp>
        </QueryProvider>
      </ConfigProvider>
    </I18nextProvider>
  </PlansContext.Provider>
);

AppWrapper.displayName = "AppWrapper";
export default AppWrapper;
